import { memo, useEffect, useState } from "react";

import styled from "styled-components";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { useDashboardStore } from "stores/DashboardStore";
import { SubmitState } from "stores/lib";

import useAnalytics from "effect/use_analytics";

import NoData from "layouts/components/NoData";

import Charts from "./Charts";
import getScatterData from "./lib/get_scatter_data";
import Table from "./Table";

// -----------------------------------------------------------------------------

const DayInSeconds = 86400;

// -----------------------------------------------------------------------------

const StaleFindingsPage = memo(function StaleFindingsPage(): JSX.Element {
  useAnalytics("Findings: Stale Findings");

  const [dashboardStore, dashboardActions] = useDashboardStore();
  const [scatterData, setScatterData] = useState(getScatterData([]));

  // Load median stale length
  useEffect(() => {
    switch (dashboardStore.loadMedianStaleLength) {
      case undefined:
        dashboardActions.loadMedianStaleLength();
        break;

      case SubmitState.SUCCESS:
        break;

      case SubmitState.ERROR:
        break;

      default:
        break;
    }
  }, [dashboardStore.loadMedianStaleLength, dashboardStore.medianStaleLength, dashboardActions]);

  // Load stale findings
  useEffect(() => {
    switch (dashboardStore.loadStaleFindings) {
      case undefined:
        dashboardActions.loadStaleFindings();
        break;

      case SubmitState.SUCCESS:
        setScatterData(getScatterData(dashboardStore.staleFindings ?? []));
        break;

      case SubmitState.ERROR:
        setScatterData([]);
        break;

      default:
        break;
    }
  }, [dashboardStore.loadStaleFindings, dashboardStore.staleFindings, dashboardActions]);

  //
  const isLoadingMedianStaleLength =
    dashboardStore.loadMedianStaleLength === SubmitState.STARTED;
  const isLoadingStaleFindings =
    dashboardStore.loadStaleFindings === SubmitState.STARTED;
  const loading = isLoadingStaleFindings || isLoadingMedianStaleLength;
  const hasStaleFindings = (dashboardStore.staleFindings ?? []).length > 0;

  //
  return (
    <>
      <Typography gutterBottom variant="h4" fontWeight="bold">
        Reporting
      </Typography>
      <Grid container sx={{ display: "flex", flex: "1", gap: "24px" }}>
        {loading ? (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        ) : hasStaleFindings ? (
          <>
            <Charts
              scatterData={scatterData}
              isLoading={isLoadingStaleFindings || isLoadingMedianStaleLength}
              median={
                dashboardStore.medianStaleLength
                  ? Math.floor(dashboardStore.medianStaleLength / DayInSeconds)
                  : 0
              }
            />
            <Table
              data={dashboardStore.staleFindings ?? []}
              isLoading={isLoadingStaleFindings}
            />
          </>
        ) : (
          <NoData
            cta={{
              title: "Connect more sources",
              url: "/environment/sources",
            }}
            bodyText="Your system is currently free of stale findings. Keep using ThreatKey to maintain the security and integrity of your systems."
          />
        )}
      </Grid>
    </>
  );
});

export default StaleFindingsPage;

// -----------------------------------------------------------------------------

const LoadingBox = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
`;
